import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import SingleAccordion from '../../../../../../modules/components/accordion/SingleAccordion';
import KeywordTypeBadge from '../../../../../../modules/components/badge/KeywordTypeBadge';
import UnlabeledBulletColumns from '../../../../../../modules/components/list/UnlabeledBulletColumns';
import { useToast } from '../../../../../../modules/components/toast/ToastContext';
import ToggleSwitch from '../../../../../../modules/components/toggleSwitch/ToggleSwitch';
import {
  FieldStatus,
  getSexualCategoryLabel,
  siAiAdultCategoryReasonTranslationKey,
  SiService,
  siteIntelligenceServiceLabel,
} from '../../../../mappings';
import UrlLabelThumbnailCard from '../../card/UrlLabelThumbnailCard';
import { UsePageDataManagerResult } from '../../hooks/usePageDataManager';
import { getImagePathByCaptureId, getTranslatedValueWithFallback } from '../../utils';
import { SiAiLinksModal } from '../../modal/SiAiLinksModal';
import { getCardId } from './CategoryDetail';
import SexualCategoryTable from './SexualCategoryTable';

interface Props {
  data: any;
  screenshots?: any[];
  status: string;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
  onExpandImages: (images: string[], title?: string) => void;
  isEditable?: boolean;
  isSiAiNewData?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
}

export default function SexualContent({
  data,
  screenshots,
  status,
  translation,
  currentLanguage,
  refetch,
  onExpandImages,
  isEditable,
  isSiAiNewData,
  onUpdate,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_CATEGORY_DETAIL.${id}` }, values);
  const { setToast } = useToast();
  const emDash = '—';
  const shouldRenderSiAiReadonlyStyle = !isEditable && !!isSiAiNewData;
  const [showAdultLinksModal, setShowAdultLinksModal] = useState(false);
  const [editingUrlKey, setEditingUrlKey] = useState<number | null>(null);
  const [editingUrlValue, setEditingUrlValue] = useState('');
  const restrictedAdultCategories = new Set([
    'sexualized-drug-use',
    'sex-rape',
    'sex-sm',
    'sex-incest',
    'sex-csam',
    'sex-violence',
    'sex-doll',
    'sex-beastiality'
  ]);

  const isAdult = data?.result?.adult ?? false;
  const adultLinks: string[] = (Array.isArray(data?.detail) ? data.detail : [])
    .map((item: any) => item?.url)
    .filter((url: unknown): url is string => typeof url === 'string');
  const hasAdultLinks = adultLinks.length > 0;

  const processedDetails = useMemo(() => {
    type ProcessedUrlData = {
      url: string;
      imagePaths: string[];
      categories: Array<{
        name: string;
        imagePaths: string[];
        isHidden: boolean;
        keywords: Array<{
          original: string;
          english: string;
          language: string;
          isHidden: boolean;
        }>;
        reason: string;
      }>;
    };
    const res: ProcessedUrlData[] = [];

    if (!(data?.detail?.length > 0)) return res;

    for (const item of data.detail) {
      const newUrlData: ProcessedUrlData = {
        url: item?.url,
        imagePaths: [
          // URL screenshot
          ...(item?.capture_id
            ? [getImagePathByCaptureId(item?.capture_id, screenshots)]
            : []),
          // User-added images (in categories)
          ...item?.categories
            ?.filter((category) => !!category?.capture_id)
            ?.map((category) => getImagePathByCaptureId(category?.capture_id, screenshots)),
        ],
        categories: [],
      };

      for (const categoryObj of item?.categories ?? []) {
        const newCategoryObj: ProcessedUrlData['categories'][number] = {
          name: categoryObj?.category,
          imagePaths: categoryObj?.capture_id
            ? [getImagePathByCaptureId(categoryObj.capture_id, screenshots)]
            : [],
          isHidden: true,
          keywords: [],
          reason: data?.result?.categories?.find((c) => c.category === categoryObj?.category)?.reason ?? emDash,
        };

        for (const keyword of categoryObj?.keywords ?? []) {
          if (keyword?.field_status === FieldStatus.ACTIVE && newCategoryObj.isHidden) {
            newCategoryObj.isHidden = false;
          }
          newCategoryObj.keywords.push({
            original: keyword?.keyword_original,
            language: keyword?.language,
            english: keyword?.keyword_en,
            isHidden: keyword?.field_status !== FieldStatus.ACTIVE,
          });
        }
        newUrlData.categories.push(newCategoryObj);
      }
      res.push(newUrlData);
    }
    return res;
  }, [data, isEditable, screenshots]);

  if (shouldRenderSiAiReadonlyStyle) {
    const activeCategories = (data?.result?.categories ?? []).filter(
      (categoryObj) => categoryObj?.field_status === FieldStatus.ACTIVE
    );

    const formatCategoryLabel = (category: string) => {
      const localizedCategory = getSexualCategoryLabel(category, intl.formatMessage);

      if (restrictedAdultCategories.has(category)) {
        return t('RESTRICTED_ADULT', {
          category: localizedCategory,
        });
      }

      return localizedCategory;
    };

    return (
      <>
        <div
          className={clsx('card border', !isAdult && 'opacity-50')}
          id={getCardId(SiService.ADULT_SHIELD_SERVICE)}
          style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
        >
          <div
            className='card-body p-6 vstack gap-4'
            style={{ cursor: 'pointer' }}
            onClick={() => setShowAdultLinksModal(true)}
          >
            <div className='d-flex align-items-start justify-content-between gap-3'>
              <div className='d-flex align-items-start gap-3'>
                <i className='bi bi-tag-fill text-warning fs-4 mt-1'></i>
                <div className='d-flex flex-column gap-2'>
                  <div className='fw-bolder'>
                    {intl.formatMessage({ id: `SI_KEYWORD_TYPE_BADGE.${SiService.ADULT_SHIELD_SERVICE}` })}
                  </div>
                  <div>{getTranslatedValueWithFallback(
                    translation,
                    currentLanguage,
                    `sensitive_category.restrict_adult_reason`,
                    data?.result?.adult_reason ?? emDash
                  )}</div>
                </div>
              </div>
              {hasAdultLinks && (
                <i
                  className='bi bi-link-45deg fs-5 text-muted'
                  aria-hidden='true'
                ></i>
              )}
            </div>

            {activeCategories.length > 0 ? (
              activeCategories.map((categoryObj, index) => (
                <div
                  key={`${categoryObj?.category ?? 'sexual-category'}-${index}`}
                  className='border rounded p-4'
                  style={{ backgroundColor: '#fff5f7', borderColor: '#ffc9d4' }}
                >
                  <div className='d-flex align-items-start justify-content-between gap-3'>
                    <div className='d-flex align-items-start gap-3'>
                      <i className='bi bi-exclamation-circle-fill text-danger mt-1'></i>
                      <div className='d-flex flex-column gap-2'>
                        <div className='fw-bolder text-danger'>
                          {formatCategoryLabel(categoryObj?.category)}
                        </div>
                        <div className='fst-italic'>{getTranslatedValueWithFallback(
                          translation,
                          currentLanguage,
                          siAiAdultCategoryReasonTranslationKey[categoryObj?.category],
                          categoryObj?.reason ?? emDash
                        )}</div>
                      </div>
                    </div>
                    <div
                      className='badge fw-bolder'
                      style={{ backgroundColor: '#fecaca', color: '#7f1d1d' }}
                    >
                      {t('TRIGGERED')}
                    </div>
                  </div>
                </div>
              ))
            ) : null}
          </div>
        </div>

        <SiAiLinksModal
          show={showAdultLinksModal}
          onClose={() => setShowAdultLinksModal(false)}
          title={t('REFERENCE_URLS')}
          links={adultLinks}
        />
      </>
    );
  }

  return (
    <>
      <div
        className={clsx('card border', !isAdult && 'opacity-50')}
        id={getCardId(SiService.ADULT_SHIELD_SERVICE)}
        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
      >
        {isEditable && (
          <div className='card-header px-6'>
            <h4 className='card-title gap-4'>
              <KeywordTypeBadge
                type={SiService.ADULT_SHIELD_SERVICE}
                className={clsx('fs-base', !isAdult && 'opacity-50')}
              />
              <ToggleSwitch
                isVisible={isAdult}
                onToggle={() => {
                  onUpdate(SiService.ADULT_SHIELD_SERVICE, (data) => {
                    const newFieldStatus = isAdult ? FieldStatus.INACTIVE : FieldStatus.ACTIVE;
                    data.result.adult = !isAdult;
                    data.result?.categories?.forEach((c) => {
                      c.field_status = newFieldStatus;
                    });
                    data.detail?.forEach((d) => {
                      d.categories?.forEach((c) => {
                        c.keywords?.forEach((k) => {
                          k.field_status = newFieldStatus;
                        });
                      });
                    });
                  });
                }}
              />
            </h4>
          </div>
        )}

        <div className='card-body p-0'>
          {isSiAiNewData && (
            <div className='bg-light py-4 px-6 d-flex flex-column gap-4'>
              <div className='d-flex flex-column flex-md-row gap-4 gap-md-9'>
                <div className='d-flex flex-column flex-shrink-0'>
                  <span className='fw-bolder fs-7'>Detect By</span>
                  <span className='fs-7'>AI</span>
                </div>
                <div className='d-flex flex-column'>
                  <span className='fw-bolder fs-7'>AI Reason</span>
                  <span className='fs-7'>{data?.result?.adult_reason ?? emDash}</span>
                </div>
              </div>
            </div>
          )}
          <div className={clsx('p-6 vstack gap-4', processedDetails?.length === 0 && 'd-none')}>
            {processedDetails?.map((item, index) => {
              const activeCategories = item.categories.filter(
                (categoryObj) => !categoryObj.isHidden
              );
              const activeCategoryLabels = activeCategories.map(
                (categoryObj) => getSexualCategoryLabel(categoryObj.name, intl.formatMessage)
              );
              const header = (
                <UrlLabelThumbnailCard
                  url={item?.url}
                  label={
                    isEditable
                      ? item.categories.map((categoryObj, cIndex) => (
                        <span key={categoryObj.name}>
                          <span
                            className={clsx(
                              categoryObj.isHidden && 'text-decoration-line-through text-muted'
                            )}
                          >
                            {getSexualCategoryLabel(categoryObj.name, intl.formatMessage)}
                          </span>
                          {cIndex < item.categories.length - 1 && ', '}
                        </span>
                      ))
                      : activeCategories.map((categoryObj, cIndex, arr) => (
                        <span key={cIndex}>
                          {getSexualCategoryLabel(categoryObj.name, intl.formatMessage)}
                          {cIndex < arr.length - 1 && ', '}
                        </span>
                      ))
                  }
                  thumbnail={item.imagePaths[0]}
                  onClickThumbnail={() => {
                    onExpandImages(item.imagePaths, 'Capture/s');
                  }}
                  showThumbnaiPlaceholder
                  className={{ container: isEditable ? 'p-3' : 'border rounded p-3' }}
                />
              );
              const nonEditableHeader = (
                <div
                  className='d-flex justify-content-between align-items-center gap-4 w-100 p-3'
                >
                  <div className='flex-grow-1 min-w-0 pe-2'>
                    <UrlLabelThumbnailCard
                      url={item?.url}
                      thumbnail={item.imagePaths[0]}
                      onClickThumbnail={() => {
                        onExpandImages(item.imagePaths, 'Capture/s');
                      }}
                      showThumbnaiPlaceholder
                    />
                  </div>
                  <div
                    className='text-end flex-shrink-0 d-flex align-items-center justify-content-end gap-2'
                  >
                    {isAdult && (
                      <KeywordTypeBadge type={SiService.ADULT_SHIELD_SERVICE} className='fs-8' />
                    )}
                  </div>
                </div>
              );
              const nonEditableContent = (
                <div className='card border rounded' key={index}>
                  {nonEditableHeader}
                  <div className='d-flex flex-column gap-2 p-6 pt-4 border-top'>
                    <div className='fw-bold'>{t('ADULT_CATEGORIES_FOUND')}</div>
                    <UnlabeledBulletColumns
                      data={activeCategoryLabels}
                      columnCount={5}
                      columnWidth={'100px'}
                    />
                  </div>
                </div>
              );
              const isEditingUrl = isSiAiNewData && editingUrlKey === index;

              return isEditable ? (
                <SingleAccordion
                  key={index}
                  header={
                    isEditingUrl ? (
                      <div
                        className='w-100 px-3 py-4'
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <input
                          className='form-control form-control-sm'
                          value={editingUrlValue}
                          onChange={(e) => setEditingUrlValue(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder='Enter URL'
                        />
                      </div>
                    ) : (
                      header
                    )
                  }
                  headerActions={
                    isSiAiNewData
                      ? isEditingUrl
                        ? (
                          <div className='d-flex align-items-center gap-2'>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-success'
                              onClick={(e) => {
                                e.stopPropagation();
                                const trimmedUrl = editingUrlValue.trim();
                                if (!trimmedUrl) {
                                  return;
                                }

                                onUpdate(SiService.ADULT_SHIELD_SERVICE, (data) => {
                                  data.detail[index].url = trimmedUrl;
                                });

                                setEditingUrlKey(null);
                                setEditingUrlValue('');
                              }}
                              disabled={!editingUrlValue.trim()}
                            >
                              Save
                            </button>
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingUrlKey(null);
                                setEditingUrlValue('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className='d-flex align-items-center gap-2'>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingUrlKey(index);
                                setEditingUrlValue(item?.url ?? '');
                              }}
                            >
                              <i className='bi bi-pencil-square'></i>
                            </button>
                          </div>
                        )
                      : undefined
                  }
                  open
                  className={{
                    body: 'border-top py-0 pb-3',
                    container: activeCategories.length === 0 && 'opacity-50',
                  }}
                >
                  <SexualCategoryTable
                    data={item.categories}
                    isSiAiNewData={isSiAiNewData}
                    onToggleVisibility={(category, keywordIndex) => {
                      const categoryIndex = item.categories.findIndex((c) => c.name === category);
                      // Toggle keyword & update active categories in result as needed
                      onUpdate(SiService.ADULT_SHIELD_SERVICE, (data) => {
                        data.detail[index].categories[categoryIndex].keywords[
                          keywordIndex
                        ].field_status = item.categories[categoryIndex].keywords[keywordIndex]
                          .isHidden
                            ? FieldStatus.ACTIVE
                            : FieldStatus.INACTIVE;

                        const hasActive = (cats: any[]) =>
                          cats?.some((c) => c?.field_status === FieldStatus.ACTIVE);
                        const categoryHasActiveKeyword = hasActive(
                          data.detail[index].categories[categoryIndex].keywords
                        );
                        data.result.categories[categoryIndex].field_status =
                          categoryHasActiveKeyword ? FieldStatus.ACTIVE : FieldStatus.INACTIVE;
                        // REVIEW: assuming must have sexual category to be ADULT
                        data.result.adult = hasActive(data.result.categories);
                      });
                    }}
                    onExpandImages={(category, images) => {
                      onExpandImages(
                        images,
                        t('CAPTURES_FOR_ADULT_CATEGORY', {
                          category: getSexualCategoryLabel(category, intl.formatMessage),
                        })
                      );
                    }}
                    isEditable={isEditable}
                  />
                </SingleAccordion>
              ) : activeCategories.length > 0 ? (
                nonEditableContent
              ) : (
                <React.Fragment key={index} />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
